import { captureBillingException } from '../utils/report-error';
import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import {
    getJWT,
    getPartialCachedBillingData,
    getCachedBillingData,
    setupSendingDomain,
    checkSendingDomain,
    updateCustomDomain,
    getCurrentSite,
} from '../data/api';
import Loading from './shared/Loading';
import GhostSubdomainSection from './Domain/GhostSubdomainSection';
import CustomDomainStatusSection from './Domain/CustomDomainStatusSection';
import SendingDomainSection from './Domain/SendingDomainSection';
import SetupCustomDomainSection from './Domain/SetupCustomDomainSection';

import {
    ROUTES,
    CUSTOM_DOMAIN_SSL_ACTIVE_STATUSES,
    CUSTOM_DOMAIN_SSL_PENDING_STATUSES,
    SENDING_DOMAIN_STATES,
    SENDING_DOMAIN_INACTIVE_STATUSES,
} from '../utils/constants';
import getForceUpgradeStatus, { FORCE_UPGRADE_STATUS } from '../utils/get-force-upgrade-status';

/**
 * @typedef {import('../types').BillingData} BillingData
 * @typedef {import('../types').CurrentSite} CurrentSite
 * @typedef {import('../types').DNSRecord} DNSRecord
 */

/**
 * @typedef {object} ButtonState
 * @property {string} className
 * @property {string} html
 * @property {boolean} disabled
 */

/** @type {Record<string, ButtonState>} */
const DOMAIN_BUTTON_STATES = {
    fail: {
        className: 'gh-btn gh-btn-block',
        html: 'Try again',
        disabled: false,
    },
    pending: {
        className: 'gh-btn gh-btn-block spinner',
        html: '<span><div class="gh-spinner"></div></span>',
        disabled: true,
    },
    default: {
        className: 'gh-btn gh-btn-block',
        html: 'Setup',
        disabled: false,
    },
};

/**
 * Check if the current plan is the Starter plan (lowest tier)
 * @param {BillingData} billingData - The cached billing data
 * @returns {boolean} True if the plan is Starter
 */
const checkIfPlanIsStarter = (billingData) => {
    return billingData?.currentPrice?.base_product?.toLowerCase() === 'starter';
};

/**
 * @typedef {object} DomainProps
 * @property {(visible: boolean) => void} showNav
 * @property {(title: string) => void} updateTitle
 * @property {{push: (route: string) => void}} history
 */

/**
 * Domain component - main entry point for domain management UI.
 * @param {DomainProps} props
 * @returns {React.ReactNode} JSX element for the Domain page
 */
function Domain({ showNav, updateTitle, history }) {
    const isMountedRef = useRef(false);

    const [loading, setLoading] = useState(/** @type {boolean} */ (true));
    const [currentSite, setCurrentSite] = useState(/** @type {CurrentSite|null} */ (null));
    const [errorMessage, setErrorMessage] = useState(/** @type {React.ReactNode} */ (null));
    const [showError, setShowError] = useState(/** @type {boolean} */ (false));
    const [buttonState, setButtonState] = useState(
        /** @type {ButtonState} */ (DOMAIN_BUTTON_STATES.default),
    );
    const [isPending, setIsPending] = useState(/** @type {boolean} */ (false));
    const [dnsRecords, setDnsRecords] = useState(/** @type {DNSRecord[]} */ ([]));
    const [isStarterPlan, setIsStarterPlan] = useState(/** @type {boolean} */ (false));
    const [userEmail, setUserEmail] = useState(/** @type {string} */ (''));
    const [domainPurchaseError, setDomainPurchaseError] = useState(/** @type {string} */ (''));

    const handleEntriPurchase = async (domain) => {
        setLoading(true);

        Sentry.addBreadcrumb({
            category: 'billing.domain',
            message: 'Domain purchased',
            level: 'info',
        });

        try {
            // Automatically activate the purchased domain
            // Use www. prefix for Entri-purchased domains
            const finalDomain = `www.${domain}`;

            await updateCustomDomain({
                siteId: currentSite?.id,
                domain: finalDomain,
                skipVerification: true,
            });

            // Refresh all data to get latest domain status and re-run all initialization logic
            setLoading(true);
            await getCurrentSite();
            await fetchData();
        } catch (error) {
            captureBillingException(error, {
                tags: {
                    source: 'domain-purchase',
                },
                extra: {
                    domain,
                    siteId: currentSite?.id,
                },
            });
            setLoading(false);
            setDomainPurchaseError(error.message);
        }
    };

    const enableDomain = async (event) => {
        event.preventDefault();

        setButtonState(DOMAIN_BUTTON_STATES.pending);

        try {
            let params = null;
            let res;
            if (currentSite?.sending_domain_status === SENDING_DOMAIN_STATES.INITIALIZED) {
                res = await checkSendingDomain({ siteId: currentSite?.id });
            } else {
                res = await setupSendingDomain({ siteId: currentSite?.id });
            }

            if (res?.error) {
                const message = res.error?.message || 'Unable to setup custom sending domain';

                Sentry.withScope((scope) => {
                    scope.setTag('source', 'enableDomain');
                    scope.setContext('full_error', res.error);
                    scope.setExtra('siteId', currentSite?.id);
                    Sentry.captureMessage(message, 'warning');
                });

                setErrorMessage(message);
                setButtonState(DOMAIN_BUTTON_STATES.fail);
                return;
            }

            // pre-populate the DNS records if they exist
            if (res && res.mailgun_dns_records && res.dmarc_dns_record) {
                params = [...res.mailgun_dns_records];
                if (res.dmarc_dns_record?.valid !== 'valid') {
                    params.push(res.dmarc_dns_record);
                }
            }

            setButtonState(DOMAIN_BUTTON_STATES.default);
            setDnsRecords(params);

            // add pass them to the next page to avoid another API call
            return history.push(ROUTES.SENDING_DOMAIN, [...params]);
        } catch (error) {
            captureBillingException(error, {
                tags: {
                    source: 'enable-domain',
                },
                extra: {
                    siteId: currentSite?.id,
                },
                user: {
                    id: currentSite?.user_id,
                },
            });
            setErrorMessage(error?.message || 'Unable to setup custom sending domain');
            setButtonState(DOMAIN_BUTTON_STATES.fail);
        }
    };

    const fetchData = async () => {
        setLoading(true);
        const cachedSiteData = await getPartialCachedBillingData('currentSite');
        /** @type {BillingData} */
        const cachedBillingData = await getCachedBillingData();

        setCurrentSite(cachedSiteData);
        setUserEmail(cachedBillingData?.user?.email_address || '');

        Sentry.setContext('site', cachedBillingData?.currentSite?.url);
        Sentry.setTag('source', 'domain');

        if (!cachedBillingData || cachedBillingData?.error) {
            // Handle different states of error or no permissions:
            //  - Legacy plans or multiple sites per user will resolve in a 403 status
            //  - Lapsed trial or subscriptions have a `forceUpgrade` state. If the current
            //    user is **not** the owner of the site, we let them know to inform the owner.
            //  - Any other error tells the user to contact support.
            setShowError(true);

            if (cachedBillingData?.error === 403) {
                setErrorMessage(
                    <>
                        You're on a special legacy plan. If you need to make any billing or domain
                        changes, reach out to us on{' '}
                        <a href="mailto:support@ghost.org" onClick={contactSupport}>
                            support@ghost.org
                        </a>
                    </>,
                );
            } else if (cachedBillingData?.error === 408) {
                setErrorMessage(
                    <>
                        The request timed out. Try again or contact{' '}
                        <a href="mailto:support@ghost.org" onClick={contactSupport}>
                            support@ghost.org
                        </a>
                        .
                    </>,
                );
            } else {
                setErrorMessage(
                    <>
                        Contact{' '}
                        <a href="mailto:support@ghost.org" onClick={contactSupport}>
                            support@ghost.org
                        </a>{' '}
                        for changes to your domain or billing.
                    </>,
                );
            }
            return history.push(ROUTES.DASHBOARD);
        }

        const forceUpgradeStatus = getForceUpgradeStatus(cachedBillingData);
        const isDunning =
            forceUpgradeStatus === FORCE_UPGRADE_STATUS.NONE && cachedBillingData?.isGrace;

        if (isDunning) {
            showNav(true);
            // Return to Dashboard when user is in dunning to update card details
            return history.push(ROUTES.DASHBOARD);
        }
        // An active subscription remains eligible for domain management while
        // the Force upgrade value in Ghost catches up.
        if (forceUpgradeStatus === FORCE_UPGRADE_STATUS.LAPSED_TRIAL_OWNER) {
            showNav(true);
            return history.push(ROUTES.PLANS);
        }

        if (cachedBillingData?.isIncomplete || cachedBillingData?.isAutoBillingGrace) {
            // Redirect back to Checkout with the current plan to finish the payment
            return history.push(cachedBillingData.checkoutRoute);
        }

        setIsPending(CUSTOM_DOMAIN_SSL_PENDING_STATUSES.includes(cachedSiteData?.ssl_status));

        if (checkIfPlanIsStarter(cachedBillingData)) {
            // Customers on Starter plan will not be able to access the sending domain settings...
            setIsStarterPlan(true);
        }

        // If the sending domain has some status, prefetch the DNS records
        // and pass them to the next page to avoid another API call
        const hasSendingDomainStatus =
            cachedSiteData?.sending_domain_status &&
            !SENDING_DOMAIN_INACTIVE_STATUSES.includes(cachedSiteData?.sending_domain_status);
        if (hasSendingDomainStatus) {
            try {
                const checkedSendingDomain = await checkSendingDomain({
                    siteId: cachedSiteData?.id,
                });

                if (
                    checkedSendingDomain?.mailgun_dns_records &&
                    checkedSendingDomain?.dmarc_dns_record
                ) {
                    const records = [...checkedSendingDomain.mailgun_dns_records];

                    // skip the DMARC record if it's already valid and verified
                    if (checkedSendingDomain?.dmarc_dns_record?.valid !== 'valid') {
                        records.push(checkedSendingDomain?.dmarc_dns_record);
                    }

                    setDnsRecords(records);
                }
            } catch (error) {
                // noop
                Sentry.withScope((errorScope) => {
                    errorScope.setTag('source', 'check-sending-domain');
                    errorScope.setExtra('siteId', cachedSiteData?.id);
                    Sentry.captureMessage(error, 'warning');
                });
            }
        }

        updateTitle('Domain settings');
        setLoading(false);
        showNav(true);
    };

    useEffect(() => {
        showNav(false);

        if (!isMountedRef.current) {
            isMountedRef.current = true;
            fetchData();
        }
    }, []);

    const contactSupport = (event) => {
        event.preventDefault();

        return (window.location.href = 'mailto:support@ghost.org');
    };

    const handleEditSubdomain = (event) => {
        event.preventDefault();

        // We need to load the token here to use it as query param when
        // updating the ghost.io subdomain and ensure it's fresh and not expired from he cache
        getJWT().then(
            (token) =>
                (window.parent.location.href = `${process.env.REACT_APP_DOMAIN_APP}?jwt=${token}`),
        );
    };

    const handleEntriPurchaseError = (err) => {
        console.error('Domain purchase error:', err);
        setDomainPurchaseError(err.message);
    };

    const canUpdateSubdomain = !CUSTOM_DOMAIN_SSL_ACTIVE_STATUSES.includes(currentSite?.ssl_status);
    const hasExternalDomain = Boolean(currentSite?.external_domain);

    return (
        <main className="main">
            <div className="domaingrid" data-test-id="dashboard">
                {loading ? (
                    <Loading />
                ) : showError ? (
                    <p style={{ textAlign: 'center', gridColumn: '1 / 3' }}>{errorMessage}</p>
                ) : (
                    <>
                        {canUpdateSubdomain && (
                            <GhostSubdomainSection
                                currentSite={currentSite}
                                onEditSubdomain={handleEditSubdomain}
                            />
                        )}

                        {hasExternalDomain ? (
                            <>
                                <CustomDomainStatusSection
                                    currentSite={currentSite}
                                    isPending={isPending}
                                />
                                <SendingDomainSection
                                    currentSite={currentSite}
                                    isStarterPlan={isStarterPlan}
                                    dnsRecords={dnsRecords}
                                    buttonState={buttonState}
                                    onSetup={enableDomain}
                                />
                            </>
                        ) : (
                            <SetupCustomDomainSection
                                currentSite={currentSite}
                                userEmail={userEmail}
                                domainPurchaseError={domainPurchaseError}
                                onDomainPurchase={handleEntriPurchase}
                                onPurchaseError={handleEntriPurchaseError}
                            />
                        )}
                    </>
                )}
            </div>
        </main>
    );
}

Domain.propTypes = {
    showNav: PropTypes.func,
    updateTitle: PropTypes.func,
    history: PropTypes.shape({
        push: PropTypes.func.isRequired,
    }),
};

export default withRouter(Domain);
